import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Badge from "../../../shared/Badge";
import Button from "../../../shared/Button";
import Spinner from "../../../shared/Spinner";
import { useGetManifestV2Query } from "../../../redux/services/manifests/manifestsV2Api";
import { usePermissions } from "../../../hooks/usePermissions";
import { useAuth } from "../../../hooks/useAuth";
import { PERMISSIONS } from "../../../constants/access-control";
import { formatDate } from "../../../utils";
import ReadinessChecklist from "../components/ReadinessChecklist";
import ManifestV2StatusActions from "../components/ManifestV2StatusActions";
import ManifestV2LegsTable from "../components/ManifestV2LegsTable";
import DispatchSnapshotSummary from "../components/DispatchSnapshotSummary";
import {
  getComplianceProfileLabel,
  getManifestStatusColor,
  getManifestStatusLabel,
} from "../utils";

const ManifestV2Show = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.MANIFESTS_EDIT);

  const { data: manifest, isLoading, isError, error } = useGetManifestV2Query(
    { id: params.id },
    { skip: !params.id }
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-6 md:px-6">
        <Spinner size="md" />
        <span className="text-sm text-gray-600">Loading manifest...</span>
      </div>
    );
  }

  if (isError || !manifest) {
    return (
      <div className="space-y-4 px-4 py-6 md:px-6">
        <h1 className="text-2xl font-semibold text-gray-900">Manifest V2</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error?.data?.message || "Unable to load this manifest."}
        </div>
      </div>
    );
  }

  const legs = Array.isArray(manifest.legs) ? manifest.legs : [];

  return (
    <div className="space-y-6 px-4 py-6 md:px-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h1 className="text-2xl font-semibold text-gray-900">
              {manifest.manifestNumber}
            </h1>
            <Badge
              color={getManifestStatusColor(manifest.status)}
              label={getManifestStatusLabel(manifest.status)}
            />
            <Badge
              color="gray"
              label={getComplianceProfileLabel(manifest.complianceProfile)}
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Driver: {manifest.driver?.fullName || "—"}
            {manifest.startDate ? (
              <>
                {" "}
                · {formatDate(manifest.startDate, null, true)}
                {manifest.endDate
                  ? ` – ${formatDate(manifest.endDate, null, true)}`
                  : ""}
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="alternative" onClick={() => navigate("/manifests/v2")}>
            Back to list
          </Button>
        </div>
      </div>

      <ManifestV2StatusActions manifest={manifest} canEdit={canEdit} />

      <ReadinessChecklist
        manifestId={manifest._id}
        legs={legs}
        storedEvaluations={manifest.complianceEvaluation}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Legs</p>
          <p className="text-2xl font-semibold text-gray-900">{legs.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Total miles</p>
          <p className="text-2xl font-semibold text-gray-900">
            {Number(manifest.totalMileage || 0).toFixed(1)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Wait hours</p>
          <p className="text-2xl font-semibold text-gray-900">
            {Number(manifest.hours || 0).toFixed(1)}
          </p>
        </div>
      </div>

      <ManifestV2LegsTable
        manifest={manifest}
        canEdit={canEdit}
        parentCompany={user?.parentCompany}
      />

      {manifest.dispatchSnapshot ? (
        <DispatchSnapshotSummary snapshot={manifest.dispatchSnapshot} />
      ) : null}
    </div>
  );
};

export default ManifestV2Show;
